import React from 'react';

import { Form, Formik } from 'formik';
import { isEqual, some, toString } from 'lodash';

import { Button } from '@components/Button';
import NumberInput from '@components/Formik/NumberInput';
import TextField from '@components/Formik/TextField';

import validationSchema from './validationSchema';

import {
  EMAIL_PLACEHOLDER,
  ENTER_MEMBER_ID_TRANSLATION,
  FIRST_NAME_PLACEHOLDER,
  IdentificationParameters,
  LAST_NAME_PLACEHOLDER,
  SearchFormValues,
} from '@domain/clubs';

import { useTouchedFormFields } from '@use-cases/clubs';
import { getClubsTranslation } from '@use-cases/clubs/helpers/getClubsTranslation';

import { useTranslation } from '@external/react-i18next';

interface Props {
  classes?: string;
  filters: IdentificationParameters;
  loadingSearch: boolean;
  searchHandler: ({
    firstName,
    lastName,
    email,
    memberId,
  }: SearchFormValues) => void;
  handleFormFieldsTouched?: (isTouched: boolean) => void;
}

const SearchForm: React.FC<Props> = ({
  classes,
  filters,
  loadingSearch,
  searchHandler,
  handleFormFieldsTouched,
}) => {
  const { t } = useTranslation();
  const { checkTouchedFields } = useTouchedFormFields(handleFormFieldsTouched);

  const emailPlaceholder = getClubsTranslation(t, EMAIL_PLACEHOLDER);

  const memberIdTranslation = getClubsTranslation(
    t,
    ENTER_MEMBER_ID_TRANSLATION
  );

  const firstNamePlaceholder = getClubsTranslation(t, FIRST_NAME_PLACEHOLDER);

  const lastNamePlaceholder = getClubsTranslation(t, LAST_NAME_PLACEHOLDER);

  const { id, firstName, lastName, email } = filters || {};

  const initialValues: SearchFormValues = {
    memberId: id ? toString(id) : '',
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
  };

  let prevFormValues: SearchFormValues;

  if (!isEqual(filters, initialValues)) {
    prevFormValues = {
      ...initialValues,
    };
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={validationSchema(t)}
      initialTouched={{
        memberId: true,
        email: true,
        lastName: true,
        firstName: true,
      }}
    >
      {({ values, errors }) => {
        checkTouchedFields(prevFormValues || initialValues, values);

        return (
          <Form className={classes}>
            <TextField
              name="email"
              label={t('mml.identify-candidate.email-label', 'Email')}
              placeholder={emailPlaceholder}
            />
            <NumberInput
              name="memberId"
              label={t('mml.identify-candidate.member-id-label', 'Member ID')}
              placeholder={memberIdTranslation}
            />
            <TextField
              name="firstName"
              label={t('mml.identify-candidate.first-name-label', 'First name')}
              placeholder={firstNamePlaceholder}
            />
            <TextField
              name="lastName"
              label={t('mml.identify-candidate.last-name-label', 'Last name')}
              placeholder={lastNamePlaceholder}
            />
            <Button
              clickHandler={() => searchHandler(values)}
              full
              className="mt-8 mb-8"
              dataTestId="button-test-id"
              disabled={
                loadingSearch ||
                !some(Object.values(values), Boolean) ||
                some(Object.values(errors), Boolean)
              }
            >
              {t('mml.identify-candidate.search-button', 'Search')}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
export default SearchForm;
